/* eslint-disable no-alert */
import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Select from 'react-select';
import { getDocumentTypesList } from 'services/actions/documents_count';
import { DocumentData, DocumentsGroup, DocumentsState } from 'store/modules/documents_store/types';
import moment from 'moment';
import { SingleDatePicker } from 'react-dates';
import { useDispatch, useSelector } from 'react-redux';
import { ProfileState } from 'store/modules/profile_store/types';
import { updateDocument } from 'store/modules/documents_store/actions';
import { ApplicationState } from 'store';
import tabId from 'services/tabId';

interface MonitoredCompanies {
  value: string;
  label: object;
  companyName: string;
}

interface ModalEditDocProps {
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  doc: DocumentData;
  documentGroupList: Array<DocumentsGroup>;
  monitoredCompaniesSelected: MonitoredCompanies | null;
}

interface SelectValuesTypes {
  value: number;
  label: string;
}

interface updateDocumentParams {
  id: number;
  description: string;
  document_type_id: number;
  expiration_date: any;
}

const ModalEditDoc = (props: ModalEditDocProps) => {
  const { showModal, setShowModal, doc, documentGroupList, monitoredCompaniesSelected } = props;
  const [docSelected, setDocSelected] = useState<SelectValuesTypes[] | object | any>([]);
  const [docSelectedType, setDocSelectedType] = useState<SelectValuesTypes[] | object | any>([]);
  const [documentTypesList, setDocumentTypesList] = useState<Array<any>>([]);
  const [docSelectedOptions, setDocSelectedOptions] = useState<any>({});
  const [dateVencimento, setDateVencimento] = useState<any | null>(null);
  const [dateFocus, setDateFocus] = useState<boolean>(false);
  const [fileDescription, setFileDescription] = useState<string>('');
  const [clickedSave, setClickedSave] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [showLoading, setShowLoading] = useState<boolean>(false);

  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);
  const { request } = useSelector<ApplicationState, DocumentsState>(
    (state) => state.session.documents,
  );

  const dispatch = useDispatch();

  const SHORT_DELAY = 2000;

  useEffect(() => {
    if (!clickedSave) return;

    if (!request.update_document.loading) return;

    setShowLoading(true);
    setShowSuccess(false);
    setShowError(false);
  }, [request.update_document.loading]);

  useEffect(() => {
    if (!clickedSave) return;

    if (!request.update_document.loaded) return;

    setShowLoading(false);
    setShowSuccess(true);
    setShowError(false);
  }, [request.update_document.loaded]);

  useEffect(() => {
    if (!clickedSave) return;

    if (!request.update_document.error) return;

    setShowLoading(false);
    setShowSuccess(false);
    setShowError(true);
  }, [request.update_document.error]);

  useEffect(() => {
    setDocSelected([{ label: `${doc?.document_group}`, value: doc?.document_group_id }]);
    setDocSelectedType([
      { label: `${doc?.document_type_description}`, value: doc?.document_type_id },
    ]);
    doc?.expirationDate === '-'
      ? setDateVencimento(null)
      : setDateVencimento(moment(doc?.expirationDate));
    setFileDescription(doc?.description);

    (async () => {
      setDocumentTypesList(await getDocumentTypesList(doc?.document_group_id));
    })();
  }, [doc]);

  useEffect(() => {
    setDocSelectedType({});
    (async () => {
      setDocumentTypesList(await getDocumentTypesList(docSelected.value));
    })();
  }, [docSelected.value]);

  useEffect(() => {
    setDocSelectedOptions(
      documentTypesList.map((document_type) => {
        return { value: document_type.id, label: document_type.description };
      }),
    );
  }, [documentTypesList]);

  useEffect(() => {
    if (!showSuccess) return;

    setTimeout(() => {
      setShowSuccess(false);
      setClickedSave(false);
      setShowModal(false);
    }, SHORT_DELAY);
  }, [showSuccess]);

  useEffect(() => {
    if (!showError) return;

    setTimeout(() => {
      setShowError(false);
    }, SHORT_DELAY);
  }, [showError]);

  const handleUpdateDocument = async () => {
    const document = {
      id: doc?.id,
      description: fileDescription,
      document_type_id: docSelectedType.value ? docSelectedType.value : doc?.document_type_id,
      expiration_date: dateVencimento,
    };

    const companyId = profile.currentCompany[tabId()] || profile.clientId;
    const cnpjValue = monitoredCompaniesSelected?.value || profile?.cnpj || '';
    const cnpj = cnpjValue.match(/\d+/g)?.join('') || '';

    setClickedSave(true);
    return dispatch(updateDocument(document as updateDocumentParams, cnpj, companyId));
  };

  return (
    <Modal size="lg" show={showModal}>
      <div className="row ">
        <div className="col-md-12">
          <div className="modal-content">
            <div className="modal-header">
              <h6 className="modal-title font-weight-bold">Editar {doc.name}</h6>
            </div>

            {showSuccess && (
              <div className="col-md-12 pt-3">
                <div className="alert alert-success m-0">Documento editado com sucesso</div>
              </div>
            )}

            {showError && (
              <div className="col-md-12 pt-3">
                <div className="alert alert-danger m-0">Erro ao atualizar documento</div>
              </div>
            )}

            <div className="row mb-1 p-3">
              <div className="form-group col-12 col-md-6 required-upload">
                <Select
                  name="estado"
                  options={documentGroupList}
                  value={docSelected}
                  closeMenuOnSelect
                  classNamePrefix="select"
                  className="basic-multi-select"
                  onChange={(event) => setDocSelected(event as object)}
                />
              </div>
              <div className="form-group col-12 col-md-6 required-upload">
                <Select
                  closeMenuOnSelect
                  options={docSelectedOptions}
                  value={docSelectedType}
                  noOptionsMessage={() => 'Selecione o tipo de documento'}
                  placeholder="Selecione o tipo de documento"
                  onChange={(event) => setDocSelectedType(event as object)}
                />
              </div>
            </div>
            <div className="row mb-1 px-3">
              <div className="form-group col-12 col-md-8 required-upload">
                <input
                  className="form-control"
                  id="description"
                  name="file-description"
                  type="text"
                  value={fileDescription}
                  onChange={(event) => setFileDescription(event.target.value)}
                  placeholder="Insira uma descrição para o documento (opcional)"
                />
              </div>
              <div className="form-group col-12 col-md-4 required-upload">
                <SingleDatePicker
                  placeholder="Vencimento"
                  regular={false}
                  date={dateVencimento}
                  small
                  required
                  orientation="horizontal"
                  anchorDirection="right"
                  block={false}
                  numberOfMonths={1}
                  onDateChange={(date) => setDateVencimento(date)}
                  focused={dateFocus}
                  openDirection="down"
                  onFocusChange={({ focused }) => setDateFocus(focused)}
                  hideKeyboardShortcutsPanel
                  id={doc.name}
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                data-dismiss="modal"
                onClick={() => {
                  setClickedSave(false);
                  setShowModal(false);
                }}
              >
                Cancelar
              </button>
              <button
                disabled={showLoading}
                type="button"
                className="btn btn-primary"
                onClick={() => handleUpdateDocument()}
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ModalEditDoc;
